import { z } from "zod";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import FormBuilder from "@/components/forms/FormBuilder";
import PrivacyLabel from "@/components/forms/PrivacyLabel";
import { usePageMeta } from "@/hooks/usePageMeta";

const schema = z.object({
  full_name: z.string().min(1, "Please enter your full name"),
  organisation: z.string().optional(),
  email: z.string().min(1, "Please enter your email").email("Please enter a valid email address"),
  telephone: z.string().optional(),
  enquiry_type: z.enum(["General enquiry", "Complaint"], {
    errorMap: () => ({ message: "Please choose an enquiry type" }),
  }),
  message: z.string().min(1, "Please enter your message"),
  privacy: z.boolean().refine((v) => v === true, "Please acknowledge the Privacy Notice to continue"),
  company: z.string().optional(),
});

const fields = [
  { name: "full_name", label: "Full name", type: "text", autoComplete: "name" },
  { name: "organisation", label: "Organisation", type: "text", width: "half", optional: true },
  { name: "email", label: "Email", type: "email", width: "half", autoComplete: "email" },
  { name: "telephone", label: "Telephone", type: "tel", width: "half", optional: true, autoComplete: "tel" },
  {
    name: "enquiry_type",
    label: "Enquiry type",
    type: "select",
    width: "half",
    placeholder: "Select enquiry type",
    options: ["General enquiry", "Complaint"],
  },
  {
    name: "message",
    label: "Message",
    type: "textarea",
    placeholder: "How can we help?",
  },
  { name: "privacy", label: "Privacy acknowledgement", type: "checkbox", labelNode: <PrivacyLabel /> },
];

export default function ContactPage() {
  usePageMeta(
    "Contact | Amanah Promotions",
    "Contact Amanah Promotions for general enquiries or to raise a complaint. We aim to respond as soon as possible."
  );
  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Get in touch with Amanah"
        intro="For general enquiries or to raise a complaint, please use the form below. We aim to respond as soon as possible."
      />
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-16 sm:px-10 sm:py-20 lg:px-16">
          <div
            className="mb-10 border-l-2 border-brand-green bg-cream p-5 text-sm leading-relaxed text-slate-body"
            data-testid="contact-complaints-note"
          >
            Raising a complaint about our fundraising? Please read our{" "}
            <Link
              to="/complaints"
              className="font-medium text-brand-green underline underline-offset-2 hover:text-brand-greenDeep"
              data-testid="contact-complaints-link"
            >
              Fundraising Complaints Procedure
            </Link>{" "}
            to see how we handle complaints, then choose &ldquo;Complaint&rdquo; as your enquiry type below.
          </div>
          <FormBuilder
            fields={fields}
            schema={schema}
            endpoint="/forms/contact"
            submitLabel="Send Message"
            defaultValues={{ enquiry_type: "", privacy: false }}
            success={{
              title: "Thank you — your message has been sent",
              message:
                "We have received your message and will respond as soon as possible. Thank you for getting in touch with Amanah Promotions.",
            }}
          />
        </div>
      </section>
    </>
  );
}
